/**
 * Drill Active Screen
 * Shows the active drill timer and action tracking interface
 */
import { WifiOff } from 'lucide-react'
import React, { useState, useEffect } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router'

import { NonSequentialTemplate } from '@/components/DrillTimer/NonSequentialTemplate'
import { RescueReadyTemplate } from '@/components/DrillTimer/RescueReadyTemplate'
import { SequentialTemplate } from '@/components/DrillTimer/SequentialTemplate'
import { TimerDisplay } from '@/components/DrillTimer/TimerDisplay'
import { useOnlineStatus } from '@/hooks/useOffline'
import { formatUserName } from '@/utils/userHelpers'

export function DrillActiveScreen() {
  const location = useLocation()
  const navigate = useNavigate()
  const { entityId } = useParams()
  const isOnline = useOnlineStatus()

  const { drillTimer, locationOfDrill, proctor } = location.state || {}

  const [drillLog, setDrillLog] = useState({
    name: `${drillTimer?.name}_${new Date().toISOString()}`,
    location: entityId,
    drillName: drillTimer?.name || '',
    locationOfDrill: locationOfDrill || '',
    roles: drillTimer?.roles || [],
    isRescueReady: drillTimer?.isRescueReady || drillTimer?.is_rescue_ready || false,
    startTime: new Date().toISOString(),
    endTime: null,
    proctor: proctor
      ? {
          userId: proctor.id,
          userName: formatUserName(proctor),
          roles: proctor.roles || [],
          signature: null,
        }
      : null,
    responders: [],
    records: [],
    notes: '',
    lessonsLearned: '',
    video: null,
  })

  const [activeTab, setActiveTab] = useState('')

  // Redirect if no drill configuration
  useEffect(() => {
    if (!drillTimer) {
      navigate(`/accounts/${entityId}/drill-timer/setup`, { replace: true })
    }
  }, [drillTimer, navigate, entityId])

  // Set initial active tab
  useEffect(() => {
    if (drillTimer && !activeTab) {
      const isRescueReady = drillTimer.isRescueReady || drillTimer.is_rescue_ready
      const isSequential = drillTimer.isSequential || drillTimer.is_sequential
      const isNonSequential = drillTimer.isNonSequential || drillTimer.is_non_sequential

      if (isRescueReady) {
        setActiveTab('rescue-ready')
      } else if (isSequential) {
        setActiveTab('sequential')
      } else if (isNonSequential) {
        setActiveTab('non-sequential')
      }
    }
  }, [drillTimer, activeTab])

  const handleLog = (action, state) => {
    const record = {
      action,
      state,
      time: new Date().toISOString(),
    }

    setDrillLog((prev) => ({
      ...prev,
      records: [...prev.records, record],
    }))
  }

  const handleStop = () => {
    const endTime = new Date().toISOString()

    // Navigate to report screen with drill log data
    navigate('../report', {
      state: {
        drillTimer,
        locationOfDrill,
        proctor,
        records: drillLog.records,
        startTime: drillLog.startTime,
        endTime,
      },
    })
  }

  if (!drillTimer) {
    return null
  }

  const isRescueReady = drillTimer.isRescueReady || drillTimer.is_rescue_ready
  const isSequential = drillTimer.isSequential || drillTimer.is_sequential
  const isNonSequential = drillTimer.isNonSequential || drillTimer.is_non_sequential

  // Count enabled drill types
  const enabledTypes = [isRescueReady, isSequential, isNonSequential].filter(Boolean).length
  const showTabs = enabledTypes > 1

  return (
    <div className="bg-background flex h-screen flex-col">
      {/* Timer Header */}
      <TimerDisplay onStop={handleStop} />

      {/* Drill Info Bar */}
      <div className="bg-muted border-b px-6 py-3">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">{drillTimer.name}</h2>
            <p className="text-muted-foreground text-sm">{locationOfDrill}</p>
          </div>
          {proctor && (
            <div className="text-right">
              <div className="text-sm font-medium">Proctor</div>
              <div className="text-muted-foreground text-sm">{formatUserName(proctor)}</div>
            </div>
          )}
        </div>
      </div>

      {/* Offline Indicator */}
      {!isOnline && (
        <div className="border-b border-orange-200 bg-orange-50 px-6 py-3">
          <div className="flex items-center gap-3">
            <WifiOff className="h-4 w-4 flex-shrink-0 text-orange-600" />
            <p className="text-sm text-orange-700">
              <span className="font-medium">Offline Mode:</span> You can complete this drill
              offline. Make sure to save the report when done - it will sync when connection is
              restored.
            </p>
          </div>
        </div>
      )}

      {/* Tabs for switching drill types */}
      {showTabs && (
        <div className="bg-background border-b">
          <div className="flex gap-1 px-6 pt-2">
            {isRescueReady && (
              <button
                type="button"
                onClick={() => setActiveTab('rescue-ready')}
                className={`rounded-t-lg px-4 py-2 text-sm font-medium transition-colors ${
                  activeTab === 'rescue-ready'
                    ? 'bg-muted text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Rescue Ready
              </button>
            )}
            {isSequential && (
              <button
                type="button"
                onClick={() => setActiveTab('sequential')}
                className={`rounded-t-lg px-4 py-2 text-sm font-medium transition-colors ${
                  activeTab === 'sequential'
                    ? 'bg-muted text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Sequential
              </button>
            )}
            {isNonSequential && (
              <button
                type="button"
                onClick={() => setActiveTab('non-sequential')}
                className={`rounded-t-lg px-4 py-2 text-sm font-medium transition-colors ${
                  activeTab === 'non-sequential'
                    ? 'bg-muted text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Non-Sequential
              </button>
            )}
          </div>
        </div>
      )}

      {/* Template Content */}
      <div className="flex-1 overflow-hidden">
        {activeTab === 'rescue-ready' && isRescueReady && (
          <RescueReadyTemplate onLog={handleLog} onEnd={handleStop} records={drillLog.records} />
        )}

        {activeTab === 'sequential' && isSequential && (
          <SequentialTemplate
            actions={drillTimer.sequential || []}
            onLog={handleLog}
            records={drillLog.records}
          />
        )}

        {activeTab === 'non-sequential' && isNonSequential && (
          <NonSequentialTemplate
            actions={drillTimer.nonSequential || drillTimer.non_sequential || []}
            onLog={handleLog}
            records={drillLog.records}
          />
        )}
      </div>
    </div>
  )
}
